import { Box, CircularProgress, Tooltip, useMediaQuery, useTheme } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQueryClient } from "@tanstack/react-query";

import { COLORS } from "../../assets/theme";
import ToastNotification from "../../components/ToastNotification";
import Actions from "../../components/common/Actions";
import CustomButton from "../../components/common/CustomButton";
import CustomCheckbox from "../../components/CustomCheckbox";
import CustomDialog from "../../components/common/CustomDialog";
import CustomLabel from "../../components/common/CustomLabel";
import SearchForm from "../../components/common/SearchForm";
import type { IAutoCompleteProps } from "../../components/common/AutoComplete";
import AutoComplete from "../../components/common/AutoComplete";
import CustomTextField from "../../components/common/CustomTextField";
import CustomTable from "../../components/common/table/CustomTable";
import SearchHeaderContainer from "../../components/common/table/SearchHeaderContainer";
import {
  createProfileCostImportConfig,
  deleteProfileCostImportConfig,
  fetchProfileCostImportConfigById,
  fetchProfileCostImportConfigByProfile,
  ProfileCostImportConfig,
  ProfileCostImportConfigParams,
  ProfileCostImportVendorKeyword,
  ProfileOption,
  SYSTEM_ID_LOCATION_OPTIONS,
  updateProfileCostImportConfig,
  useProfileCostImportConfigsQuery,
  useProfileOptionsQuery,
} from "../../services/ProfileCostImportConfigService";
import { IField } from "../../types/table/interfaces.type";
import { hasJwtPermission } from "../../services/ServicesHelpers";
import { useVendorsForProfileQuery } from "../../services/PendingCostImportService";

const newKeywordRowId = () =>
  globalThis.crypto?.randomUUID?.() ??
  `kw-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

type VendorKeywordFormRow = ProfileCostImportVendorKeyword & {
  _uiKey: string;
};

type ProfileCostImportConfigForm = Partial<
  Omit<ProfileCostImportConfig, "vendorKeywords">
> & {
  vendorKeywords?: VendorKeywordFormRow[];
};

/**
 * Cost-import routing: decide which profile vendor owns the attachment via systemId in
 * systemIdLocation, or via keyword → vendorConnectionProfileId rows (keyword-only when non-empty).
 */
const formatSystemIdLocation = (value?: string) => {
  switch (value) {
    case "EMAIL_SUBJECT":
      return "Email Subject";
    case "EMAIL_BODY":
      return "Email Body";
    case "FILE_NAME":
      return "File Name";
    default:
      return value || "-";
  }
};

/** Match SearchForm filter autocompletes (filled input background). */
const dialogFilterAutocompleteSx: NonNullable<IAutoCompleteProps["sx"]> = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: COLORS.iconColor,
  },
};

const ProfileCostImportConfigPage: React.FC = () => {
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [appliedFilters, setAppliedFilters] =
    useState<ProfileCostImportConfigParams>({
      search: "",
    });
  const [filters, setFilters] = useState<ProfileCostImportConfigParams>({});

  const dialogRef = useRef<ProfileCostImportConfigDialogRef>(null);

  const {
    data: profileOptionsData,
    isLoading: profileOptionsLoading,
    error: profileOptionsError,
  } = useProfileOptionsQuery();

  useEffect(() => {
    if (profileOptionsError) {
      ToastNotification.error(profileOptionsError.message);
    }
  }, [profileOptionsError]);

  const profileOptions = useMemo(
    () => profileOptionsData?.options ?? [],
    [profileOptionsData],
  );
  const profileMap = useMemo(
    () => profileOptionsData?.profileMap ?? {},
    [profileOptionsData],
  );

  const handleOpenDialog = (mode: "add" | "edit" | "delete", id?: string) => {
    dialogRef.current?.openDialog(mode, id);
  };

  const handleSearchChange = (search: string) => {
    setAppliedFilters((prev) => ({ ...prev, search }));
  };

  const handleSearchClick = () => {
    setAppliedFilters((prev) => ({
      ...prev,
      ...filters,
    }));
  };

  const handleRefresh = () => {
    setAppliedFilters((prev) => ({ ...prev }));
    queryClient.invalidateQueries({ queryKey: ["profileCostImportConfig"] });
  };

  const actions = Actions({
    handleOpenEditModal: {
      handler: async (id: string | number) => {
        handleOpenDialog("edit", String(id));
      },
      isDisabled: !hasJwtPermission("GET_PROFILE_COST_IMPORT_CONFIG"),
    },
    onDelete: {
      handler: async (id: string | number) => {
        handleOpenDialog("delete", String(id));
      },
      isDisabled: !hasJwtPermission("DELETE_PROFILE_COST_IMPORT_CONFIG"),
    },
  });

  const fields: IField<ProfileCostImportConfig>[] = useMemo(
    () => [
      {
        key: "profileId",
        header: "Profile(Rate Updates Email)",
        transform: (row) => {
          const profileLabel = profileMap[row.profileId] ?? row.profileId;
          const hasEmail = profileLabel.includes("(");
          return hasEmail ? profileLabel : `⚠️ ${profileLabel}`;
        },
      },
      { key: "rateHeader", header: "Rate Header" },
      {
        key: "systemIdLocation",
        header: "System ID Location",
        transform: (row) => formatSystemIdLocation(row.systemIdLocation),
      },
      {
        key: "vendorKeywords",
        header: "Vendor match",
        transform: (row) =>
          row.vendorKeywords && row.vendorKeywords.length > 0
            ? `Keywords (${row.vendorKeywords.length})`
            : "System ID",
      },
      {
        key: "requireConfirmation",
        header: "Require Confirmation",
        transform: (row) => (row.requireConfirmation ? "Yes" : "No"),
      },
      {
        key: "enabled",
        header: "Enabled",
        transform: (row) => (
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <CustomCheckbox
              checked={row.enabled}
              onChange={() => {}}
              ariaLabel="enabled status"
            />
          </Box>
        ),
      },
    ],
    [profileMap],
  );

  const autoCompletes: IAutoCompleteProps[] = [
    {
      label: "Profile",
      value: filters.profileId ?? "",
      options: profileOptions,
      clearable: true,
      disabled: profileOptionsLoading,
      placeholder: "Select Profile",
      onChange: (value: string | number | (string | number)[]) => {
        setFilters((prev) => ({
          ...prev,
          profileId: value ? String(value) : undefined,
        }));
      },
    },
    {
      label: "System ID Location",
      value: filters.systemIdLocation ?? "",
      options: SYSTEM_ID_LOCATION_OPTIONS,
      clearable: true,
      placeholder: "Select Any",
      onChange: (value: string | number | (string | number)[]) => {
        setFilters((prev) => ({
          ...prev,
          systemIdLocation: value
            ? (String(value) as ProfileCostImportConfig["systemIdLocation"])
            : undefined,
        }));
      },
    },
    {
      label: "Require Confirmation",
      value:
        filters.requireConfirmation === undefined
          ? ""
          : filters.requireConfirmation
            ? "true"
            : "false",
      options: [
        { label: "Yes", value: "true" },
        { label: "No", value: "false" },
      ],
      clearable: true,
      placeholder: "Select Any",
      onChange: (value: string | number | (string | number)[]) => {
        if (!value) {
          setFilters((prev) => ({ ...prev, requireConfirmation: undefined }));
          return;
        }
        setFilters((prev) => ({
          ...prev,
          requireConfirmation: String(value) === "true",
        }));
      },
    },
    {
      label: "Enabled",
      value:
        filters.enabled === undefined ? "" : filters.enabled ? "true" : "false",
      options: [
        { label: "Enabled", value: "true" },
        { label: "Disabled", value: "false" },
      ],
      clearable: true,
      placeholder: "Select Any",
      onChange: (value: string | number | (string | number)[]) => {
        if (!value) {
          setFilters((prev) => ({ ...prev, enabled: undefined }));
          return;
        }
        setFilters((prev) => ({
          ...prev,
          enabled: String(value) === "true",
        }));
      },
    },
  ];

  return (
    <Box padding={isSmallScreen ? 2 : 3}>
      <Box sx={{ mb: 3 }}>
        <CustomLabel
          text="Profile Cost Import Configuration"
          variant={isSmallScreen ? "h6" : "h5"}
          fontWeight={500}
          mb={1}
        />
        <SearchForm
          autoCompletes={autoCompletes}
          buttons={[
            {
              label: "Search",
              variant: "contained",
              bgColor: COLORS.GOLD,
              onClick: handleSearchClick,
              permission: "GET_ALL_PROFILE_COST_IMPORT_CONFIGS",
            },
          ]}
        />

        <SearchHeaderContainer
          search={appliedFilters.search ?? ""}
          setSearch={handleSearchChange}
          buttonConfig={[
            {
              size: "small",
              bgColor: COLORS.GOLD,
              startIcon: <AddIcon />,
              onClick: () => handleOpenDialog("add"),
              textColor: COLORS.iconColor,
              label: "Add Configuration",
              permission: "POST_PROFILE_COST_IMPORT_CONFIG",
            },
          ]}
        />
        <CustomTable
          filters={appliedFilters}
          fields={fields}
          fetchFunc={useProfileCostImportConfigsQuery}
          actions={actions}
          sortableFields={[
            "rateHeader",
            "systemIdLocation",
            "requireConfirmation",
          ]}
        />
      </Box>

      <ProfileCostImportConfigDialog
        ref={dialogRef}
        onRefresh={handleRefresh}
        profileOptions={profileOptions}
        profileMap={profileMap}
        profileOptionsLoading={profileOptionsLoading}
      />
    </Box>
  );
};

interface ProfileCostImportConfigDialogRef {
  openDialog: (mode: "add" | "edit" | "delete", id?: string) => void;
}

interface ProfileCostImportConfigDialogProps {
  onRefresh: () => void;
  profileOptions: ProfileOption[];
  profileMap: Record<string, string>;
  profileOptionsLoading: boolean;
}

const defaultConfigValues: ProfileCostImportConfigForm = {
  rateHeader: "Rate",
  effectiveDateHeader: "Effective Date",
  mccHeader: "MCC",
  mncHeader: "MNC",
  countryHeader: "CountryName",
  operatorHeader: "OperatorName",
  systemIdLocation: "EMAIL_SUBJECT",
  matchFileName: "",
  requireConfirmation: true,
  overrideExisting: false,
  rateCount: 1,
  enabled: true,
  vendorKeywords: [],
};

const ProfileCostImportConfigDialog = forwardRef<
  ProfileCostImportConfigDialogRef,
  ProfileCostImportConfigDialogProps
>(({ onRefresh, profileOptions, profileMap, profileOptionsLoading }, ref) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<"add" | "edit" | "delete">(
    "add",
  );
  const [dialogData, setDialogData] =
    useState<ProfileCostImportConfigForm>(defaultConfigValues);
  const [selectedId, setSelectedId] = useState<string>("");
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [profileConflict, setProfileConflict] =
    useState<ProfileCostImportConfig | null>(null);
  const [isCheckingProfile, setIsCheckingProfile] = useState(false);

  const isKeywordMode =
    dialogMode !== "delete" && (dialogData.vendorKeywords?.length ?? 0) > 0;

  const { data: vendors = [], isLoading: vendorsLoading } =
    useVendorsForProfileQuery(dialogData.profileId ?? "", isKeywordMode);

  const vendorSelectOptions = useMemo(
    () =>
      (vendors ?? []).map((v) => ({
        label: `${v.name}${v.systemId ? ` (${v.systemId})` : ""}`,
        value: v.id,
      })),
    [vendors],
  );

  const vendorIdSet = useMemo(
    () => new Set((vendors ?? []).map((v) => String(v.id))),
    [vendors],
  );

  useImperativeHandle(ref, () => ({
    openDialog: (mode, id) => {
      setDialogMode(mode);
      setDialogOpen(true);
      setProfileConflict(null);
      setSelectedId(id ?? "");
      openDialog(mode, id);
    },
  }));

  useEffect(() => {
    if (dialogMode === "delete") {
      setIsSaveDisabled(false);
      return;
    }

    const requiredFieldsFilled =
      !!dialogData.profileId &&
      !!dialogData.rateHeader &&
      !!dialogData.systemIdLocation &&
      dialogData.rateCount !== undefined &&
      dialogData.rateCount !== null;

    const isRateCountValid =
      dialogData.rateCount === undefined ||
      dialogData.rateCount === null ||
      Number(dialogData.rateCount) > 0;

    const rows = dialogData.vendorKeywords ?? [];
    let keywordValid = true;
    if (rows.length > 0) {
      const complete = rows.filter(
        (r) =>
          (r.vendorKeyword ?? "").trim() &&
          String(r.vendorConnectionProfileId ?? "").trim(),
      );
      const partial = rows.some((r) => {
        const kw = (r.vendorKeyword ?? "").trim();
        const vid = String(r.vendorConnectionProfileId ?? "").trim();
        return (Boolean(kw) && !vid) || (!kw && Boolean(vid));
      });
      keywordValid = complete.length >= 1 && !partial;
    }

    setIsSaveDisabled(
      !requiredFieldsFilled ||
        !isRateCountValid ||
        Boolean(profileConflict) ||
        !keywordValid,
    );
  }, [dialogData, dialogMode, profileConflict]);

  useEffect(() => {
    if (
      dialogMode === "delete" ||
      !dialogData.profileId ||
      dialogData.profileId === ""
    ) {
      setProfileConflict(null);
      return;
    }

    let isMounted = true;
    setIsCheckingProfile(true);

    fetchProfileCostImportConfigByProfile(dialogData.profileId)
      .then((existing) => {
        if (!isMounted) return;
        if (
          existing &&
          (dialogMode === "add" || String(existing.id) !== selectedId)
        ) {
          setProfileConflict(existing);
        } else {
          setProfileConflict(null);
        }
      })
      .catch(() => {
        if (isMounted) {
          setProfileConflict(null);
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsCheckingProfile(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [dialogData.profileId, dialogMode, selectedId]);

  useEffect(() => {
    if (!isKeywordMode || !dialogData.profileId) return;
    setDialogData((prev) => {
      const list = prev.vendorKeywords;
      if (!list?.length) return prev;
      let dirty = false;
      const next = list.map((row) => {
        const vid = String(row.vendorConnectionProfileId ?? "").trim();
        if (vid && !vendorIdSet.has(vid)) {
          dirty = true;
          return { ...row, vendorConnectionProfileId: "" };
        }
        return row;
      });
      return dirty ? { ...prev, vendorKeywords: next } : prev;
    });
  }, [dialogData.profileId, isKeywordMode, vendorIdSet]);

  const openDialog = async (mode: "add" | "edit" | "delete", id?: string) => {
    setDialogMode(mode);

    if (mode === "add") {
      setDialogData(defaultConfigValues);
      return;
    }

    if ((mode === "edit" || mode === "delete") && id) {
      try {
        const data = await fetchProfileCostImportConfigById(id);
        const rawKeywords = data.vendorKeywords;
        const vendorKeywords: VendorKeywordFormRow[] = Array.isArray(
          rawKeywords,
        )
          ? rawKeywords.map(
              (
                item: ProfileCostImportVendorKeyword & { keyword?: string },
              ) => ({
                vendorKeyword: String(item.vendorKeyword ?? item.keyword ?? ""),
                vendorConnectionProfileId: String(
                  item.vendorConnectionProfileId ?? "",
                ),
                _uiKey: newKeywordRowId(),
              }),
            )
          : [];
        setDialogData({
          ...defaultConfigValues,
          ...data,
          vendorKeywords,
        });
      } catch (error: any) {
        ToastNotification.error(error.message);
        setDialogOpen(false);
      }
    }
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setDialogData(defaultConfigValues);
    setProfileConflict(null);
    setSelectedId("");
  };

  const buildSavePayload = (): Partial<ProfileCostImportConfig> => {
    const raw = dialogData.vendorKeywords ?? [];
    const vendorKeywords: ProfileCostImportVendorKeyword[] =
      raw.length === 0
        ? []
        : raw
            .map((row) => ({
              vendorKeyword: String(row.vendorKeyword ?? "").trim(),
              vendorConnectionProfileId: String(
                row.vendorConnectionProfileId ?? "",
              ).trim(),
            }))
            .filter(
              (row) =>
                row.vendorKeyword !== "" &&
                row.vendorConnectionProfileId !== "",
            );
    const { vendorKeywords: _vk, ...rest } = dialogData;
    const trimmedFile =
      typeof rest.matchFileName === "string" && rest.matchFileName.trim() !== ""
        ? rest.matchFileName.trim()
        : undefined;
    return { ...rest, vendorKeywords, matchFileName: trimmedFile };
  };

  const handleSave = async () => {
    if (profileConflict) {
      ToastNotification.error(
        "Each profile can only have one configuration. Please choose another profile or edit the existing configuration.",
      );
      return;
    }

    try {
      const payload = buildSavePayload();
      if (dialogMode === "add") {
        await createProfileCostImportConfig(payload);
        ToastNotification.success("Configuration created successfully!");
      } else if (dialogMode === "edit" && selectedId) {
        await updateProfileCostImportConfig(selectedId, payload);
        ToastNotification.success("Configuration updated successfully!");
      }
      onRefresh();
      closeDialog();
    } catch (error: any) {
      ToastNotification.error(error.message);
    }
  };

  const handleDelete = async () => {
    try {
      if (selectedId) {
        await deleteProfileCostImportConfig(selectedId);
        ToastNotification.success("Configuration deleted successfully!");
        onRefresh();
        closeDialog();
      }
    } catch (error: any) {
      ToastNotification.error(error.message);
    }
  };

  const helperTextForProfile = () => {
    if (isCheckingProfile) return "Checking existing configuration...";
    if (profileConflict) {
      const conflictedProfileName =
        profileMap[profileConflict.profileId] || "this profile";
      return `${conflictedProfileName} already has a configuration.`;
    }
    if (dialogData.profileId) {
      const selectedProfile = profileOptions.find(
        (p) => p.value === dialogData.profileId,
      );
      if (selectedProfile && !selectedProfile.label.includes("(")) {
        return "⚠️ This profile has no Rate Updates Email set. Populate that field in the profile settings for this configuration to take effect.";
      }
    }
    return undefined;
  };

  const fields =
    dialogMode === "delete"
      ? []
      : [
          {
            type: "autocomplete" as const,
            autocompleteProps: {
              label: "Profile(Rate Updates Email)",
              value: dialogData.profileId ?? "",
              options: profileOptions,
              clearable: true,
              disabled: profileOptionsLoading,
              placeholder: "Select profile",
              highlightLabelStar: true,
              error: Boolean(profileConflict),
              helperText: helperTextForProfile(),
              sx: dialogFilterAutocompleteSx,
              onChange: (value: string | number | (string | number)[]) => {
                const v = Array.isArray(value) ? value[0] : value;
                setDialogData((prev) => ({
                  ...prev,
                  profileId:
                    v !== undefined && v !== null && String(v) !== ""
                      ? String(v)
                      : "",
                }));
              },
            },
          },
          {
            type: "autocomplete" as const,
            tooltip:
              "Where to look for a match for the vendor system ID or keyword.",
            autocompleteProps: {
              label: isKeywordMode
                ? "Keyword match location"
                : "System ID location",
              value: dialogData.systemIdLocation ?? "",
              options: SYSTEM_ID_LOCATION_OPTIONS,
              clearable: true,
              placeholder: isKeywordMode
                ? "Where text is read for keywords or system ID"
                : "Choose where to read the vendor system ID",
              highlightLabelStar: true,
              sx: dialogFilterAutocompleteSx,
              onChange: (value: string | number | (string | number)[]) => {
                const v = Array.isArray(value) ? value[0] : value;
                setDialogData((prev) => ({
                  ...prev,
                  systemIdLocation: v
                    ? (String(v) as ProfileCostImportConfig["systemIdLocation"])
                    : "EMAIL_SUBJECT",
                }));
              },
            },
          },
          {
            type: "custom" as const,
            fullWidth: true,
            component: (
              <Tooltip
                title="If empty, all valid attachments will be processed."
                PopperProps={{
                  modifiers: [
                    {
                      name: "offset",
                      options: { offset: [0, -8] },
                    },
                  ],
                }}
              >
                <Box sx={{ width: "100%", display: "block" }}>
                  <CustomTextField
                    label="Match file name"
                    value={dialogData.matchFileName ?? ""}
                    onChange={(e) => {
                      setDialogData((prev) => ({
                        ...prev,
                        matchFileName: e.target.value,
                      }));
                    }}
                    placeholder="Optional — limit to attachments matching this name pattern"
                    fullWidth
                    size="small"
                  />
                </Box>
              </Tooltip>
            ),
          },
          ...(isKeywordMode
            ? [
                {
                  type: "custom" as const,
                  fullWidth: true,
                  label: "Keyword → vendor",
                  highlightLabelStar: true,
                  component: (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.5,
                        width: "100%",
                      }}
                    >
                      <CustomLabel
                        text="Keywords match in the selected location (case-insensitive; longer phrases win when overlaps occur)."
                        fontSize="12px"
                        fontWeight={400}
                        color={COLORS.typographyColor}
                      />
                      {(dialogData.vendorKeywords ?? []).map((row, index) => {
                        const rowKey = row._uiKey;
                        const keywordRows = dialogData.vendorKeywords ?? [];
                        const isLastOrOnlyRow =
                          keywordRows.length === 1 ||
                          index === keywordRows.length - 1;
                        return (
                          <Box
                            key={rowKey}
                            sx={{
                              display: "grid",
                              gridTemplateColumns: {
                                xs: "1fr",
                                sm: "1fr 1fr",
                              },
                              gap: 2,
                              alignItems: "flex-end",
                              width: "100%",
                            }}
                          >
                            <Box sx={{ minWidth: 0, width: "100%" }}>
                              <CustomTextField
                                label="Keyword / phrase"
                                value={row.vendorKeyword}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  setDialogData((prev) => {
                                    const list = [
                                      ...(prev.vendorKeywords ?? []),
                                    ];
                                    const idx = list.findIndex(
                                      (r) => r._uiKey === rowKey,
                                    );
                                    if (idx === -1) return prev;
                                    list[idx] = {
                                      ...list[idx],
                                      vendorKeyword: v,
                                    };
                                    return { ...prev, vendorKeywords: list };
                                  });
                                }}
                                placeholder="Text appearing in that location"
                                size="small"
                                fullWidth
                              />
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "row",
                                flexWrap: { xs: "wrap", sm: "nowrap" },
                                gap: 1,
                                alignItems: "flex-end",
                                minWidth: 0,
                                width: "100%",
                              }}
                            >
                              <Box sx={{ flex: "1 1 0", minWidth: 0 }}>
                                <AutoComplete
                                  label="Vendor"
                                  value={row.vendorConnectionProfileId || ""}
                                  options={vendorSelectOptions}
                                  clearable
                                  highlightLabelStar={false}
                                  placeholder={
                                    !dialogData.profileId
                                      ? "Select a profile first"
                                      : vendorsLoading
                                        ? "Loading vendors..."
                                        : "Vendor to use for this keyword"
                                  }
                                  disabled={
                                    !dialogData.profileId ||
                                    vendorsLoading ||
                                    (vendorSelectOptions.length === 0 &&
                                      !vendorsLoading)
                                  }
                                  helperText={
                                    !dialogData.profileId
                                      ? undefined
                                      : vendorSelectOptions.length === 0 &&
                                          !vendorsLoading
                                        ? "No vendors on this profile"
                                        : undefined
                                  }
                                  sx={{
                                    ...dialogFilterAutocompleteSx,
                                    width: "100%",
                                  }}
                                  onChange={(value) => {
                                    setDialogData((prev) => {
                                      const list = [
                                        ...(prev.vendorKeywords ?? []),
                                      ];
                                      const idx = list.findIndex(
                                        (r) => r._uiKey === rowKey,
                                      );
                                      if (idx === -1) return prev;
                                      const v = Array.isArray(value)
                                        ? String(value[0] ?? "")
                                        : String(value ?? "");
                                      list[idx] = {
                                        ...list[idx],
                                        vendorConnectionProfileId: v,
                                      };
                                      return { ...prev, vendorKeywords: list };
                                    });
                                  }}
                                />
                              </Box>
                              <Box
                                sx={{
                                  flexShrink: 0,
                                  display: "flex",
                                  justifyContent: "flex-end",
                                  alignItems: "center",
                                }}
                              >
                                {isLastOrOnlyRow ? (
                                  <CustomButton
                                    bgColor={COLORS.GOLD}
                                    textColor={COLORS.textPrimary}
                                    borderRadius="8px"
                                    onClick={() => {
                                      if (!dialogData.profileId) return;
                                      setDialogData((prev) => ({
                                        ...prev,
                                        vendorKeywords: [
                                          ...(prev.vendorKeywords ?? []),
                                          {
                                            vendorKeyword: "",
                                            vendorConnectionProfileId: "",
                                            _uiKey: newKeywordRowId(),
                                          },
                                        ],
                                      }));
                                    }}
                                    disabled={!dialogData.profileId}
                                    isIconOnly
                                    aria-label="Add keyword row"
                                    sx={{
                                      width: "40px",
                                      height: "40px",
                                      minWidth: "40px",
                                    }}
                                  >
                                    <AddIcon fontSize="small" />
                                  </CustomButton>
                                ) : (
                                  <CustomButton
                                    bgColor={COLORS.GOLD}
                                    textColor={COLORS.textPrimary}
                                    borderRadius="8px"
                                    onClick={() => {
                                      setDialogData((prev) => ({
                                        ...prev,
                                        vendorKeywords: (
                                          prev.vendorKeywords ?? []
                                        ).filter((r) => r._uiKey !== rowKey),
                                      }));
                                    }}
                                    isIconOnly
                                    aria-label="Remove keyword row"
                                    sx={{
                                      height: "40px",
                                      minWidth: "40px",
                                    }}
                                  >
                                    <RemoveIcon fontSize="small" />
                                  </CustomButton>
                                )}
                              </Box>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  ),
                },
              ]
            : []),
          {
            type: "text" as const,
            label: "Rate Header",
            value: dialogData.rateHeader ?? "Rate",
            onChange: (value: string | number) => {
              setDialogData((prev) => ({
                ...prev,
                rateHeader: String(value),
              }));
            },
            placeholder: "Rate",
            highlightLabelStar: true,
          },
          {
            type: "text" as const,
            label: "Effective Date Header",
            value: dialogData.effectiveDateHeader ?? "Effective Date",
            onChange: (value: string | number) => {
              setDialogData((prev) => ({
                ...prev,
                effectiveDateHeader: String(value),
              }));
            },
            placeholder: "Effective Date",
          },
          {
            type: "text" as const,
            label: "Combined MCCMNC Header",
            value: dialogData.mccmncHeader ?? "",
            onChange: (value: string | number) => {
              setDialogData((prev) => ({
                ...prev,
                mccmncHeader: value === "" ? undefined : String(value),
              }));
            },
            placeholder: "Optional",
          },
          {
            type: "text" as const,
            label: "MCC Header",
            value: dialogData.mccHeader ?? "MCC",
            onChange: (value: string | number) => {
              setDialogData((prev) => ({
                ...prev,
                mccHeader: String(value),
              }));
            },
            placeholder: "MCC",
          },
          {
            type: "text" as const,
            label: "MNC Header",
            value: dialogData.mncHeader ?? "MNC",
            onChange: (value: string | number) => {
              setDialogData((prev) => ({
                ...prev,
                mncHeader: String(value),
              }));
            },
            placeholder: "MNC",
          },
          {
            type: "text" as const,
            label: "Country/Destination Header",
            value: dialogData.countryHeader ?? "CountryName",
            onChange: (value: string | number) => {
              setDialogData((prev) => ({
                ...prev,
                countryHeader: String(value),
              }));
            },
            placeholder: "CountryName",
          },
          {
            type: "text" as const,
            label: "Operator Header",
            value: dialogData.operatorHeader ?? "OperatorName",
            onChange: (value: string | number) => {
              setDialogData((prev) => ({
                ...prev,
                operatorHeader: String(value),
              }));
            },
            placeholder: "OperatorName",
          },
          {
            type: "text" as const,
            label: "Rate Count",
            value:
              dialogData.rateCount !== undefined ? dialogData.rateCount : 1,
            typeinput: "number",
            onChange: (value: string | number) => {
              setDialogData((prev) => ({
                ...prev,
                rateCount:
                  value === "" || value === null ? undefined : Number(value),
              }));
            },
            placeholder: "1",
            helperText: "Divisor applied to rates (must be greater than 0).",
          },
          {
            type: "switch" as const,
            label: "Require Confirmation",
            value: dialogData.requireConfirmation ?? true,
            onChange: (value: boolean) => {
              setDialogData((prev) => ({
                ...prev,
                requireConfirmation: Boolean(value),
              }));
            },
          },
          {
            type: "switch" as const,
            label: "Override Existing",
            value: dialogData.overrideExisting ?? false,
            onChange: (value: boolean) => {
              setDialogData((prev) => ({
                ...prev,
                overrideExisting: Boolean(value),
              }));
            },
          },
          {
            type: "switch" as const,
            label: "Match vendor by keywords (instead of system ID)",
            value: isKeywordMode,
            tooltip:
              "Map each keyword to a specific vendor for this profile. Replaces System ID matching.",
            onChange: (checked: boolean) => {
              setDialogData((prev) => ({
                ...prev,
                vendorKeywords: checked
                  ? prev.vendorKeywords && prev.vendorKeywords.length > 0
                    ? prev.vendorKeywords
                    : [
                        {
                          vendorKeyword: "",
                          vendorConnectionProfileId: "",
                          _uiKey: newKeywordRowId(),
                        },
                      ]
                  : [],
              }));
            },
          },
          {
            type: "switch" as const,
            label: "Enabled",
            value: dialogData.enabled ?? true,
            onChange: (value: boolean) => {
              setDialogData((prev) => ({
                ...prev,
                enabled: Boolean(value),
              }));
            },
          },
        ];

  return (
    <CustomDialog
      open={dialogOpen}
      onClose={closeDialog}
      mode={dialogMode}
      title={
        dialogMode === "add"
          ? "Add Profile Cost Import Configuration"
          : dialogMode === "edit"
            ? "Edit Profile Cost Import Configuration"
            : "Delete Configuration"
      }
      fieldsPerRow={2}
      fields={fields}
      buttons={
        dialogMode === "delete"
          ? [
              {
                label: "Cancel",
                variant: "outlined",
                bgColor: "transparent",
                textColor: COLORS.typographyColor,
                borderColor: COLORS.dividerColor,
                borderRadius: "12px",
                onClick: closeDialog,
              },
              {
                label: "Delete",
                variant: "contained",
                bgColor: COLORS.errorRed,
                borderRadius: "12px",
                onClick: handleDelete,
                permission: "DELETE_PROFILE_COST_IMPORT_CONFIG",
              },
            ]
          : [
              {
                label: "Save",
                variant: "contained",
                bgColor: COLORS.GOLD,
                borderRadius: "12px",
                onClick: handleSave,
                disabled:
                  isSaveDisabled ||
                  (dialogMode === "edit"
                    ? !hasJwtPermission("PUT_PROFILE_COST_IMPORT_CONFIG")
                    : !hasJwtPermission("POST_PROFILE_COST_IMPORT_CONFIG")),
                startIcon: profileOptionsLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : undefined,
              },
            ]
      }
    />
  );
});

export default ProfileCostImportConfigPage;
